import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { TestInstructions } from '../components/TestInstructions';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { TestMetaTags } from '../components/TestMetaTags';
import { supabase } from '../supabase';
import { NetworkSpeedTest } from '../components/NetworkSpeedTest';
import { measureDownloadSpeed } from '../utils/networkSpeed';
import { enterFullscreen, isFullscreen } from '../utils/fullscreen';
import { FullscreenDialog } from '../components/FullscreenDialog';

export const TestInstructionsPage = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location; // Capture state here
  const { user, setCurrentTest, setRouteLoading } = useApp();
  const [loading, setLoading] = useState(true);
  const [loadedTest, setLoadedTest] = useState(null);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [savedTest, setSavedTest] = useState(null);
  const [speedTestStatus, setSpeedTestStatus] = useState<'testing' | 'sufficient' | 'insufficient' | 'error'>('testing');
  const [downloadSpeed, setDownloadSpeed] = useState<number | null>(null);
  const [showFullscreenDialog, setShowFullscreenDialog] = useState(false);
  const [testToNavigateTo, setTestToNavigateTo] = useState(null);

  useEffect(() => {
    const runSpeedTest = async () => {
      try {
        const speed = await measureDownloadSpeed();
        setDownloadSpeed(speed);
        if (speed >= 25) {
          setSpeedTestStatus('sufficient');
        } else {
          setSpeedTestStatus('insufficient');
        }
      } catch (error) {
        setSpeedTestStatus('error');
      }
    };
    runSpeedTest();
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!isFullscreen()) {
        setShowFullscreenDialog(false);
        navigate('/dashboard', { state: { message: 'Test canceled due to exiting fullscreen.' } });
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [navigate]);

  useEffect(() => {
    const checkSavedTest = () => {
      const savedTestString = sessionStorage.getItem('currentTest');
      if (savedTestString) {
        const savedTestData = JSON.parse(savedTestString);
        const savedTestUserId = sessionStorage.getItem('currentTestUserId');
        
        if (user && savedTestUserId === user.uid && savedTestData.id.toString() === testId) {
          setSavedTest(savedTestData);
          setShowResumeDialog(true);
          return true; // Found a saved test for the current user
        }
      }
      return false; // No saved test found
    };

    if (checkSavedTest()) {
      setLoading(false);
      return;
    }
    
    console.log('TestInstructionsPage useEffect triggered');
    console.log('TestInstructionsPage location.state:', state); // Use 'state' here
    console.log('TestInstructionsPage testId:', testId);

    // Auth check is now handled by ProtectedTestRoute wrapper

    const loadTest = async () => {
      console.log('TestInstructionsPage: loadTest called');
      if (!testId) {
        console.log('TestInstructionsPage: testId is null, returning');
        return;
      }

      if (testId.startsWith('custom_')) {
        console.log('TestInstructionsPage: Handling custom test');
        const { test } = location.state || {};
        console.log('TestInstructionsPage: Extracted test object:', test);
        if (test) {
          console.log('TestInstructionsPage: Custom test found in location.state');
          setLoadedTest(test);
          setCurrentTest(test);
          sessionStorage.removeItem('currentTest');
        } else {
          console.log('TestInstructionsPage: Custom test NOT found in location.state, checking sessionStorage');
          const storedTest = sessionStorage.getItem('currentTest');
          if (storedTest) {
            console.log('TestInstructionsPage: Custom test found in sessionStorage');
            const parsedTest = JSON.parse(storedTest);
            setLoadedTest(parsedTest);
            setCurrentTest(parsedTest);
            sessionStorage.removeItem('currentTest');
          } else {
            console.log('TestInstructionsPage: Custom test NOT found in sessionStorage, redirecting to dashboard (Path 1)');
            navigate('/dashboard');
          }
        }
        setLoading(false);
        console.log('TestInstructionsPage: Custom test handled, returning');
        return;
      }

      console.log('TestInstructionsPage: Handling non-custom test');
      const numericTestId = Number(testId);
      if (isNaN(numericTestId)) {
        console.log('TestInstructionsPage: testId is NaN for non-custom test, redirecting to dashboard (Path 2)');
        navigate('/dashboard');
        return;
      }

      setRouteLoading(true);
      try {
        console.log('TestInstructionsPage: Fetching non-custom test from exams table');
        const { data: testData, error } = await supabase
          .from('exams')
          .select('*')
          .eq('id', numericTestId)
          .single();

        if (error) throw error;

        const test = {
          id: testData.id,
          name: testData.Name,
          exam: testData.exam,
          duration: testData.Duration,
          questionsCount: testData.Questions.split(',').length,
          tags: testData.Tags ? testData.Tags.split(',').map(t => t.trim()) : [],
          negativeMarks: testData.negative_marks || 1,
          totalMarks: testData.Questions.split(',').length * 4,
        };

        setLoadedTest(test);
        setCurrentTest(test);
        console.log('TestInstructionsPage: Non-custom test loaded successfully');
      } catch (err) {
        console.error("Failed to load test:", err);
        console.log('TestInstructionsPage: Failed to load non-custom test, redirecting to dashboard (Path 3)');
        navigate('/dashboard');
      } finally {
        setRouteLoading(false);
        setLoading(false);
      }
    };

    loadTest();
  }, [testId, user, state, setCurrentTest, navigate, setRouteLoading]);

  const initiateTestStart = (testToStart) => {
    if (!testToStart) return;
    setTestToNavigateTo(testToStart);
    enterFullscreen(document.documentElement);
    setShowFullscreenDialog(true);
  };

  const handleStartTest = () => {
    initiateTestStart(loadedTest);
  };

  const handleDismissFullscreenDialog = () => {
    setShowFullscreenDialog(false);
    if (testToNavigateTo) {
      setCurrentTest(testToNavigateTo);
      navigate(`/test/${testToNavigateTo.id}/start`);
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleResumeTest = () => {
    initiateTestStart(savedTest);
    setShowResumeDialog(false);
  };

  const handleStartNewTest = () => {
    // Clear old test data
    sessionStorage.removeItem('currentTest');
    sessionStorage.removeItem('currentTestUserId');
    if (savedTest) {
      sessionStorage.removeItem(`test-${savedTest.id}-answers`);
      sessionStorage.removeItem(`test-${savedTest.id}-statuses`);
      sessionStorage.removeItem(`test-${savedTest.id}-currentIndex`);
      sessionStorage.removeItem(`test-${savedTest.id}-timeLeft`);
    }
    setShowResumeDialog(false);
    // The useEffect will re-run and load the new test
  };

  if (loading || !loadedTest && !showResumeDialog) {
    return <LoadingSpinner message="Loading test instructions..." fullScreen />;
  }

  const ResumeTestDialog = () => (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" aria-modal="true">
      <div className="bg-brand-surface rounded-lg shadow-xl p-6 w-full max-w-md">
        <h2 className="text-xl font-bold">Resume Test?</h2>
        <p className="text-brand-text-secondary mt-2">You have an incomplete test. Would you like to resume it or start a new one?</p>
        <div className="mt-6 flex justify-end space-x-3">
          <button onClick={handleStartNewTest} className="px-4 py-2 text-sm font-semibold rounded-lg border border-brand-border hover:bg-gray-100">Start New Test</button>
          <button onClick={handleResumeTest} className="px-4 py-2 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover">Resume Test</button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {showFullscreenDialog && <FullscreenDialog onDismiss={handleDismissFullscreenDialog} />}
      {showResumeDialog && <ResumeTestDialog />}
      {loadedTest && !showResumeDialog && (
        <>
          <TestMetaTags testData={loadedTest} />
          <div className="bg-white text-black dark:bg-gray-800 dark:text-white">
            <TestInstructions 
              test={loadedTest} 
              onStartTest={handleStartTest} 
              onBack={handleBack}
              isStartDisabled={speedTestStatus !== 'sufficient'}
              networkTestComponent={<NetworkSpeedTest status={speedTestStatus} speedMbps={downloadSpeed} />}
            />
          </div>
        </>
      )}
    </>
  );
};